import { useCallback, useEffect, useState } from 'react'
import {
  INBOX_FOLDER_CHANGE_EVENT,
  canonicalizeInboxFolder,
  kindFilterToFolder,
  dispatchInboxFolderChange,
  dispatchOpenInboxMain,
  type InboxFolderId,
  type InboxListFocus,
} from './inbox-folder-types'
import type { KindFilter } from './types'

/** Survives InboxFoldersIndex remount when open-inbox-main activates the Inbox tab. */
let sharedFolderId: InboxFolderId = 'inbox'
let sharedListFocus: InboxListFocus = 'all'
let sharedActiveExtension: string | null = null

/**
 * Shared folder selection for mode panel + main index.
 * Listens to inbox-folder-change and legacy inbox-filter-change.
 * Legacy `unread` folder canonicalizes to Inbox + Unread list focus.
 */
export function useInboxFolderSelection(defaultFolder: InboxFolderId = 'inbox') {
  const initial = canonicalizeInboxFolder(sharedFolderId || defaultFolder)
  const [folderId, setFolderId] = useState<InboxFolderId>(initial.folderId)
  const [listFocus, setListFocus] = useState<InboxListFocus>(
    () => sharedListFocus || initial.listFocus,
  )
  const [activeExtension, setActiveExtension] = useState<string | null>(
    () => sharedActiveExtension,
  )

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (
        e as CustomEvent<{
          folderId?: InboxFolderId
          kindFilter?: KindFilter
          activeExtension?: string | null
          listFocus?: InboxListFocus
        }>
      ).detail ?? {}

      if (detail.folderId) {
        const next = canonicalizeInboxFolder(detail.folderId)
        sharedFolderId = next.folderId
        setFolderId(next.folderId)
        // Explicit listFocus in the event wins; else unread folder sets focus.
        const focus =
          detail.listFocus ??
          (detail.folderId === 'unread' ? 'unread' : next.listFocus)
        // Switching to a noun folder resets focus unless the event was unread.
        if (detail.folderId === 'unread' || detail.listFocus) {
          sharedListFocus = focus
          setListFocus(focus)
        } else if (detail.folderId !== 'inbox') {
          // Noun folder click: keep list focus (Unread chip applies within folder)
        } else {
          // Inbox root click: keep current list focus unless event cleared it
        }
      } else if (detail.kindFilter) {
        const mapped = kindFilterToFolder(detail.kindFilter)
        const next = canonicalizeInboxFolder(mapped)
        sharedFolderId = next.folderId
        setFolderId(next.folderId)
        if (mapped === 'unread') {
          sharedListFocus = 'unread'
          setListFocus('unread')
        }
      }
      if (detail.listFocus && !detail.folderId) {
        sharedListFocus = detail.listFocus
        setListFocus(detail.listFocus)
      }
      if (detail.activeExtension !== undefined) {
        sharedActiveExtension = detail.activeExtension
        setActiveExtension(detail.activeExtension)
      }
    }

    window.addEventListener(INBOX_FOLDER_CHANGE_EVENT, handler)
    window.addEventListener('inbox-filter-change', handler)
    return () => {
      window.removeEventListener(INBOX_FOLDER_CHANGE_EVENT, handler)
      window.removeEventListener('inbox-filter-change', handler)
    }
  }, [])

  const selectFolder = useCallback((id: InboxFolderId) => {
    const next = canonicalizeInboxFolder(id)
    sharedFolderId = next.folderId
    sharedActiveExtension = null
    setFolderId(next.folderId)
    setActiveExtension(null)
    if (id === 'unread') {
      sharedListFocus = 'unread'
      setListFocus('unread')
    }
    dispatchInboxFolderChange(next.folderId, null)
    // Continuity: folder click must surface the Inbox index in the main pane,
    // even when a document (or other) tab is currently active.
    dispatchOpenInboxMain()
  }, [])

  const selectListFocus = useCallback((focus: InboxListFocus) => {
    sharedListFocus = focus
    setListFocus(focus)
  }, [])

  return {
    folderId,
    listFocus,
    activeExtension,
    selectFolder,
    selectListFocus,
    setActiveExtension,
  }
}
